import { AlertDialog, Avatar, Col, face as faceSize, Text, useTheme } from "@rally/ui-kit";
import { useCallback, useEffect, useState } from "react";
import { failureSaid } from "../../localization/failure.ts";
import { say } from "../../localization/say.ts";
import { WhenLabel } from "../../localization/when-label.tsx";
import { faceOfAnAgent } from "../../me/an-agents-mark.ts";
import { calledBy } from "../../me/called.ts";
import { useFace } from "../../me/face.ts";
import { act, read } from "../../net/rally.ts";
import { usePresent } from "../../push/here.ts";
import { goTo } from "../../router.ts";
import { useHeard } from "../composer/heard.ts";
import { usePanelActions, useStack } from "../panel/panels.tsx";
import type { Card } from "../panel/stacking.ts";

export type PersonHere = {
  account: string;
  handle: string;
  displayName: string | null;
  avatar: string | null;
  role: string;
  agent?: boolean;
  model?: string | null;

  removedAt: string | null;
};

const A_FEW = 3;

export function personCard(conversation: string, me: string, who: PersonHere): Card {
  return {
    key: `person:${who.account}`,
    title: calledBy(who) ?? who.handle,
    render: () => <Person conversation={conversation} me={me} who={who} />,
  };
}

function Caption({ children }: { children: string }) {
  return (
    <Text typeName="caption" color="text.tertiary">
      {children}
    </Text>
  );
}

export function Person({
  conversation,
  me,
  who,
}: {
  conversation: string;
  me: string;

  who: PersonHere;
}) {
  const theme = useTheme();
  const stack = useStack();
  const lines = useHeard(conversation);
  const [here, setHere] = useState<PersonHere[] | null>(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);

  const again = useCallback(() => {
    void read<PersonHere[]>("conversation.people", { conversation })
      .then(setHere)
      .catch(() => undefined);
  }, [conversation]);
  useEffect(again, [again]);

  const them = here?.find((one) => one.account === who.account) ?? who;
  const mine = here?.find((one) => one.account === me) ?? null;
  const name = calledBy(them) ?? them.handle;
  const face = useFace(them.avatar);
  const itsMe = them.account === me;

  const mayRemove = !itsMe && (mine?.role === "owner" || mine?.role === "admin");
  const gone = them.removedAt !== null;

  const theirs = lines
    .filter((line) => line.by === them.account && line.kind === "message" && (line.text ?? "").trim())
    .slice(-A_FEW)
    .reverse();

  const doing = async (what: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await what();
      setSaid(null);
    } catch (cause) {
      setSaid(failureSaid(cause, say("person.broke")));
    } finally {
      setBusy(false);
    }
  };

  const messageThem = () =>
    doing(async () => {
      const made = await act<{ id: string }>("conversation.create", {});
      await act("conversation.addParticipant", { conversation: made.id, account: them.account });
      stack.close();
      goTo(`/c/${made.id}`);
    });

  const removeThem = () =>
    doing(async () => {
      await act("conversation.removeParticipant", { conversation, account: them.account });
      setAsking(false);

      stack.back();
    });

  usePanelActions([
    ...(!itsMe && !gone
      ? [
          {
            key: "message",
            icon: "message-square" as const,
            label: say("person.message"),
            testID: "person-message",
            disabled: busy,
            onPress: () => void messageThem(),
          },
        ]
      : []),
    ...(mayRemove
      ? [
          {
            key: "remove",
            icon: "trash" as const,
            label: say("person.remove"),
            testID: "person-remove",
            disabled: busy,
            onPress: () => setAsking(true),
          },
        ]
      : []),
  ]);

  return (
    <Col style={{ gap: theme.space[4] }}>
      <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[3] }}>
        <Avatar
          name={name}
          src={face}
          size={faceSize.card}
          present={usePresent(them.account)}
          {...(them.agent ? faceOfAnAgent(them.model) : {})}
        />
        <Text
          typeName="caption"
          color="text.tertiary"
          numberOfLines={1}
          testID="person-name"
          style={{ flex: 1, minWidth: 0 }}
        >
          {`@${them.handle} · ${
            them.role === "owner"
              ? say("person.owner")
              : them.role === "admin"
                ? say("person.admin")
                : say("person.member")
          }`}
        </Text>
      </Col>

      {gone ? (
        <Text typeName="caption" color="text.tertiary" testID="person-gone">
          {say("person.gone")}
        </Text>
      ) : null}

      <Col style={{ gap: theme.space[2] }}>
        <Caption>{say("person.lines")}</Caption>
        {theirs.length === 0 ? (
          <Text typeName="caption" color="text.tertiary" testID="person-no-lines">
            {say("person.noLines")}
          </Text>
        ) : (
          theirs.map((line) => (
            <Col key={line.id} style={{ gap: 2 }}>
              <Text typeName="body" color="text.secondary" numberOfLines={3}>
                {line.text}
              </Text>
              <WhenLabel at={line.at} />
            </Col>
          ))
        )}
      </Col>

      {said ? (
        <Text typeName="caption" color="danger" testID="person-said">
          {said}
        </Text>
      ) : null}

      <AlertDialog
        visible={asking}
        onDismiss={() => setAsking(false)}
        title={say("person.removeTitle")(name)}
        description={say("person.removeMeans")}
        confirmLabel={say("person.remove")}
        cancelLabel={say("person.keep")}
        tone="danger"
        confirming={busy}
        onConfirm={() => void removeThem()}
        onCancel={() => setAsking(false)}
      />
    </Col>
  );
}
